import React, { useMemo } from 'react';

import { isPlariumEnv } from 'App/constants';

import CreateConceptForm from 'scenes/Gallery/GalleryPage/Concepts/CreateConceptForm/CreateConceptForm';
import * as CONCEPT from 'scenes/Gallery/GalleryPage/Concepts/constants';
import {
  useIsUpdating,
  useMoveCreativeToConcept,
} from 'scenes/Gallery/GalleryPage/Concepts/data';
import { useConcept } from 'scenes/Gallery/GalleryPage/Concepts/data';
import * as CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/constants';
import {
  useCreative,
  useCreativeDetails,
} from 'scenes/Gallery/GalleryPage/Creatives/data';
import * as VIEW_CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/view-constants';
import {
  STATUS_ACTIVE,
  STATUS_PENDING,
} from 'scenes/Gallery/GalleryPage/constants';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';

import { getValidationSchema } from './schemas';

const MoveCreativeToConcept = ({ id, onSubmit, onClose, onExited, open }) => {
  const { data: creative, isLoading: isLoadingCreative } = useCreative({ id });

  const { data: creativeDetails, isLoading: isLoadingCreativeDetails } =
    useCreativeDetails({ id });

  const { data: concept, isLoading: isLoadingConcept } = useConcept(
    {
      id: creative?.[CREATIVE.CONCEPT_ID],
    },
    {
      enabled: !!creative?.[CREATIVE.CONCEPT_ID],
    }
  );
  const [moveCreativeToConcept] = useMoveCreativeToConcept();
  const isUpdating = useIsUpdating();
  const {
    isAllowConceptFamilies,
    isAllowRequestedBy,
    isAllowJiraTask,
    isAllowOutsourceStudio,
    isAllowDesigner,
    isAllowBusinessPartner,
    isAllowChangeConceptStatus,
    isAllowTested,
  } = useGalleryPermissions();

  const initialValues = useMemo(() => {
    return {
      [CONCEPT.STATE_SCHEDULES]: [
        {
          state:
            isPlariumEnv && isAllowChangeConceptStatus
              ? STATUS_PENDING
              : STATUS_ACTIVE,
        },
      ],
      ...(creative && creativeDetails && concept
        ? {
            [CONCEPT.TAGS]: [
              ...(creative[CREATIVE.INHERIT_TAGS] || []),
              ...(creative[CREATIVE.TAGS] || []),
            ],
            [CONCEPT.APPLICATIONS]: creative?.[CREATIVE.APPLICATION]
              ? [creative?.[CREATIVE.APPLICATION]]
              : concept?.[CONCEPT.APPLICATIONS],
            [CONCEPT.DESCRIPTION]: `${
              creative[CREATIVE.DESCRIPTION] + '\n' || ''
            } Previous creative name: ${creativeDetails[VIEW_CREATIVE.NAME]}`,
            [CONCEPT.ORIGINAL]: {
              ...creative,
              [VIEW_CREATIVE.POSTFIX]: '',
              [VIEW_CREATIVE.NAME]: creativeDetails[VIEW_CREATIVE.NAME],
              [VIEW_CREATIVE.SHORT_NAME]:
                creativeDetails[VIEW_CREATIVE.SHORT_NAME],
            },
          }
        : null),
    };
  }, [
    creative,
    concept,
    creativeDetails,
    isPlariumEnv,
    isAllowChangeConceptStatus,
  ]);

  const handleSubmit = async (values) => {
    const {
      [CONCEPT.ORIGINAL]: fullOriginal,
      [CONCEPT.JIRA_TASK]: jiraTask,
      [CONCEPT.REQUESTED_BY]: requestedBy,
      [CONCEPT.BUSINESS_PARTNERS]: businessPartners,
      ...concept
    } = values;

    const { [CREATIVE.FILE]: _, ...original } = fullOriginal;

    original[CREATIVE.DESCRIPTION] = concept[CONCEPT.DESCRIPTION];
    original[CREATIVE.INHERIT_TAGS] = concept[CONCEPT.TAGS];
    original[CREATIVE.TAGS] = concept[CONCEPT.TAGS];

    if (isAllowJiraTask) {
      concept[CONCEPT.JIRA_TASK] = jiraTask;
      original[CREATIVE.JIRA_TASK] = jiraTask;
    }
    if (isAllowRequestedBy) {
      concept[CONCEPT.REQUESTED_BY] = requestedBy;
      original[CREATIVE.REQUESTED_BY] = requestedBy;
    }
    if (isAllowBusinessPartner) {
      concept[CONCEPT.BUSINESS_PARTNERS] = businessPartners;
      original[CREATIVE.BUSINESS_PARTNERS] = businessPartners;
    }
    const conceptId = await moveCreativeToConcept({
      ...concept,
      [CONCEPT.ORIGINAL]: original,
    });
    onSubmit({ id: conceptId });
  };

  return (
    <CreateConceptForm
      loading={
        isLoadingCreative ||
        isLoadingCreativeDetails ||
        isLoadingConcept ||
        isUpdating
      }
      validationSchema={getValidationSchema({
        isAllowConceptFamilies,
        isAllowRequestedBy,
        isAllowJiraTask,
        isAllowBusinessPartner,
        isAllowOutsourceStudio,
        isAllowDesigner,
        isAllowTested,
      })}
      title={'Move Creative to New Concept'}
      submitButtonLabel={'Move & Save'}
      onClose={onClose}
      open={open}
      onExited={onExited}
      active={!!initialValues}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      fileInfo={creativeDetails[VIEW_CREATIVE.FILE]}
    />
  );
};
export default MoveCreativeToConcept;
